let itemLevels;

const DYNAMIC_EXP_BASE = 120;
const DYNAMIC_EXP_LINEAR_SCALE = 80;
const DYNAMIC_EXP_QUADRATIC_SCALE = 24;
const DYNAMIC_EXP_CUBIC_SCALE = 18;
const DYNAMIC_EXP_LATE_TAIL_START = 100;
const DYNAMIC_EXP_LATE_TAIL_SCALE = 6000;
const DYNAMIC_EXP_LATE_TAIL_POWER = 1.3;
const DYNAMIC_EXP_MAX = 1_000_000_000_000;

function normalizeLevel(level) {
  const parsed = Number(level);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 1;
}

function getIdDifficultyMultiplier(entityId) {
  const raw = String(entityId ?? "").toLowerCase().trim();
  const numericId = Number(raw);

  if (Number.isFinite(numericId) && numericId > 0) {
    const growth = Math.log10(numericId + 1);
    return Math.min(2.1, 1 + growth * 0.2);
  }

  const trailingDigits = raw.match(/(\d+)$/);
  if (trailingDigits) {
    const value = Number(trailingDigits[1]);
    if (Number.isFinite(value) && value > 0) {
      const growth = Math.log10(value + 1);
      return Math.min(2.1, 1 + growth * 0.2);
    }
  }

  let hash = 0;
  for (let i = 0; i < raw.length; i += 1) {
    hash = (hash * 31 + raw.charCodeAt(i)) % 1000003;
  }
  return 1 + (hash % 35) / 100;
}

function getDynamicItemLevelInfo(level, itemId) {
  const safeLevel = normalizeLevel(level);
  const difficulty = getIdDifficultyMultiplier(itemId);
  const coreGrowth =
    safeLevel * DYNAMIC_EXP_LINEAR_SCALE +
    Math.pow(safeLevel, 2) * DYNAMIC_EXP_QUADRATIC_SCALE +
    Math.pow(safeLevel, 3) * DYNAMIC_EXP_CUBIC_SCALE;
  const lateLevel = Math.max(0, safeLevel - DYNAMIC_EXP_LATE_TAIL_START);
  const lateTail =
    lateLevel > 0
      ? DYNAMIC_EXP_LATE_TAIL_SCALE * Math.pow(lateLevel, DYNAMIC_EXP_LATE_TAIL_POWER)
      : 0;
  const expNeeded = Math.max(
    10,
    Math.min(
      DYNAMIC_EXP_MAX,
      Math.floor((DYNAMIC_EXP_BASE + coreGrowth + lateTail) * difficulty),
    ),
  );
  const expGain = Math.max(
    1,
    Math.floor((1 + Math.sqrt(safeLevel) * 0.8) / difficulty),
  );

  return {
    level: safeLevel,
    expNeeded,
    expGain,
  };
}

function buildDynamicItemLevelTable(itemId, maxLevel = 200) {
  const safeMaxLevel = Math.max(1, normalizeLevel(maxLevel));
  const table = [];
  for (let level = 1; level <= safeMaxLevel; level += 1) {
    table.push(getDynamicItemLevelInfo(level, itemId));
  }
  return table;
}

function loadItemLevels() {
  if (!itemLevels) {
    itemLevels = new Proxy({}, {
      get(target, prop) {
        if (typeof prop === "string" && /^[0-9]+$/.test(prop)) {
          return buildDynamicItemLevelTable(Number(prop));
        }
        return target[prop];
      },
    });
  }
}

function getItemLevelInfo(level, itemId) {
  loadItemLevels();
  return getDynamicItemLevelInfo(level, itemId);
}

function getExpForNextItemLevel(currentLevel, itemId) {
  return getItemLevelInfo(currentLevel, itemId).expNeeded;
}

function levelUp(item) {
  if (!item || typeof item !== "object") return;

  let loops = 0;
  while (loops < 200) {
    const currentLevel = normalizeLevel(item.level);
    const currentInfo = getItemLevelInfo(currentLevel, item.itemId);
    const expNeeded = Number(currentInfo?.expNeeded || 0);

    if (!Number.isFinite(expNeeded) || expNeeded <= 0) {
      item.expNeeded = DYNAMIC_EXP_MAX;
      item.expGain = currentInfo?.expGain ?? 1;
      break;
    }

    const currentExp = Number(item.exp || 0);
    if (currentExp < expNeeded) {
      item.level = currentLevel;
      item.exp = Math.max(0, currentExp);
      item.expNeeded = expNeeded;
      item.expGain = currentInfo.expGain ?? 1;
      break;
    }

    item.level = currentLevel + 1;
    item.exp = Math.max(0, currentExp - expNeeded);

    const nextInfo = getItemLevelInfo(item.level, item.itemId);
    item.expNeeded = nextInfo.expNeeded ?? DYNAMIC_EXP_MAX;
    item.expGain = nextInfo.expGain ?? 1;
    loops += 1;
  }
}

loadItemLevels();

module.exports = { itemLevels, getExpForNextItemLevel, getItemLevelInfo, levelUp };
